import AutoCompleteChip from '@components/standardAutoCompleteSearch/AutoCompleteChip'
import StandardAutoCompleteSearch, {
  AutoCompleteOption,
  AutoCompleteRenderChipProps,
  AutoCompleteResultsZIndexPlacement,
} from '@components/standardAutoCompleteSearch/StandardAutoCompleteSearch'
import { colors } from '@globals/themes/defaultTheme'
import {
  FederatedSearchRecipientsParams,
  FederatedSearchRecipientsResults,
  SendTargetType,
  SendTo,
  TeamDisplayData,
  TenantMemberAndTeamDisplayData,
  TenantMemberDisplayData,
} from '@kudos/http-client'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { Typography } from '@mui/material'
import Stack from '@mui/material/Stack'
import TeamAvatar from '@shared/avatar/TeamAvatar'
import UserAvatar from '@shared/avatar/UserAvatar'
import { fullNameFromParts } from '@shared/utils/nameFormatting'
import { t } from '@transifex/native'
import { T } from '@transifex/react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
  Control,
  Controller,
  FieldName,
  FieldPathValue,
  FieldValues,
  Path,
  useFormContext,
  useWatch,
} from 'react-hook-form'
import ErrorText from '../ErrorText'
import { generateTeamName, getTeamIcon } from '../teamsAutoComplete/utils'
import useSearchTenantMembersAndTeams from './useSearchTenantMembersAndTeams'

const getAvatar = (member: TenantMemberAndTeamDisplayData) => {
  return 'teamType' in member ? (
    <TeamAvatar avatarBoxStyle={{ display: 'flex' }} size={25} />
  ) : (
    <UserAvatar
      size={25}
      firstName={member.firstName}
      lastName={member.lastName}
      media={member.avatarUrls}
      avatarBoxStyle={{ display: 'flex' }}
    />
  )
}

const renderChip = (
  option: AutoCompleteOption<TenantMemberAndTeamDisplayData>,
  props: AutoCompleteRenderChipProps,
  showAvatars?: boolean,
) => {
  const isTeam = 'teamType' in option.value
  if (isTeam) {
    const team = option.value as TeamDisplayData
    return (
      <AutoCompleteChip
        key={option.id}
        {...props}
        label={t('{teamName} ({activeMemberCount})', {
          teamName: generateTeamName(team.name, team.segmentType),
          activeMemberCount: team.activeMemberCount,
        })}
        icon={!showAvatars ? getTeamIcon(team.teamType) : undefined}
        avatar={showAvatars ? getAvatar(option.value) : undefined}
      />
    )
  } else {
    const tenantMember = option.value as TenantMemberDisplayData
    return (
      <AutoCompleteChip
        key={tenantMember.id}
        {...props}
        label={fullNameFromParts(tenantMember.firstName, tenantMember.lastName)}
        avatar={showAvatars ? getAvatar(tenantMember) : undefined}
      />
    )
  }
}

const renderOption = (
  props: object,
  option: AutoCompleteOption<TenantMemberAndTeamDisplayData>,
) => {
  const isTeam = 'teamType' in option.value

  return (
    <Stack
      {...props}
      sx={{
        '& svg': {
          color: colors.grayDark,
        },
      }}
      key={option.id}
      direction="row"
      useFlexGap
      gap={1}>
      {isTeam ? getTeamIcon((option.value as TeamDisplayData).teamType) : <PersonOutlineIcon />}
      {isTeam ? (
        <Typography>
          {generateTeamName(
            (option.value as TeamDisplayData).name,
            (option.value as TeamDisplayData).segmentType,
          )}
        </Typography>
      ) : (
        <Typography>
          {fullNameFromParts(
            (option.value as TenantMemberDisplayData).firstName,
            (option.value as TenantMemberDisplayData).lastName,
          )}
        </Typography>
      )}
      {isTeam && (
        <Typography variant="caption">
          <T
            _str="{activeMemberCount} {activeMemberCount, plural, one {user} other {users}}"
            activeMemberCount={(option.value as TeamDisplayData).activeMemberCount}
          />
        </Typography>
      )}
    </Stack>
  )
}

type TenantMembersAndTeamsSearchFieldProps<T extends FieldValues> = {
  name: Path<T>
  control: Control<T>
  label?: ReactNode
  searchFn: (params: FederatedSearchRecipientsParams) => Promise<FederatedSearchRecipientsResults>
  showAvatars?: boolean
  disabled?: boolean
  zIndexPlacement?: AutoCompleteResultsZIndexPlacement
}

const TenantMembersAndTeamsSearchField = <T extends FieldValues>({
  name,
  control,
  label,
  searchFn,
  showAvatars,
  disabled,
  zIndexPlacement,
}: TenantMembersAndTeamsSearchFieldProps<T>) => {
  const [searchText, setSearchText] = useState<string>('')
  const { setValue } = useFormContext()
  const [options, setOptions] = useState<AutoCompleteOption<TenantMemberAndTeamDisplayData>[]>([])

  const selectedMembers = useWatch({ control, name })
  const selectedMembersIds = useMemo(
    () => selectedMembers?.map(({ id }: { id: string }) => id),
    [selectedMembers],
  )

  const { searchedTenantMembersAndTeams } = useSearchTenantMembersAndTeams(
    searchText,
    selectedMembersIds,
    searchFn,
  )

  const onSelectedOptionsChanged = (
    options: AutoCompleteOption<TenantMemberAndTeamDisplayData>[],
  ) => {
    const selectedOptions: FieldPathValue<FieldValues, FieldName<FieldValues>> = options.map(
      option => option.value,
    )
    setValue(name, selectedOptions, { shouldValidate: true, shouldDirty: true })
  }

  useEffect(() => {
    setOptions(
      searchedTenantMembersAndTeams?.map(
        (member: TenantMemberAndTeamDisplayData) =>
          ({
            avatar: showAvatars ? getAvatar(member) : undefined,
            icon: 'teamType' in member && !showAvatars ? getTeamIcon(member.teamType) : undefined,
            value: member,
            id: member.id,
          }) as AutoCompleteOption<TenantMemberAndTeamDisplayData>,
      ) || [],
    )
  }, [searchedTenantMembersAndTeams])

  return (
    <Stack>
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            <StandardAutoCompleteSearch<TenantMemberAndTeamDisplayData>
              searchText={searchText}
              value={selectedMembers?.map((member: TenantMemberAndTeamDisplayData | SendTo) => {
                // TODO: remove this once BE is merged. This is to ensure backwards compatility was the BE will
                // change this logic to only return TenantMemberAndTeamDisplayData data.
                if (
                  'type' in member &&
                  member.name &&
                  member.type === SendTargetType.TENANT_MEMBER
                ) {
                  return {
                    id: member.id,
                    value: {
                      id: member.id,
                      firstName: member.name.split(' ')[0],
                      lastName: member.name.split(' ')[1],
                    } as TenantMemberAndTeamDisplayData,
                  }
                }
                return {
                  value: member,
                  id: member.id,
                }
              })}
              options={options}
              onSearchTextChanged={setSearchText}
              onSelectedOptionsChanged={onSelectedOptionsChanged}
              placeholder={t('Search users or teams...')}
              renderChip={(option, props) => renderChip(option, props, showAvatars)}
              label={label}
              renderOption={renderOption}
              groupBy={option => ('teamType' in option.value ? t('Teams') : t('Users'))}
              disabled={disabled}
              zIndexPlacement={zIndexPlacement}
            />
            {!!error && <ErrorText>{error.message}</ErrorText>}
          </>
        )}
      />
    </Stack>
  )
}

export default TenantMembersAndTeamsSearchField
